import { useState, useEffect, useMemo } from 'react';
import { bankApi } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, Phone, Mail, User, LayoutGrid, List, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

// Format phone number to (XXX) XXX-XXXX
const formatPhoneNumber = (value) => {
  if (!value) return '';
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  // Take only first 10 digits
  const truncated = digits.slice(0, 10);
  // Format based on length
  if (truncated.length === 0) return '';
  if (truncated.length <= 3) return `(${truncated}`;
  if (truncated.length <= 6) return `(${truncated.slice(0, 3)}) ${truncated.slice(3)}`;
  return `(${truncated.slice(0, 3)}) ${truncated.slice(3, 6)}-${truncated.slice(6)}`;
};

const initialFormData = {
  name: '',
  nickname: '',
  address: '',
  city_state_zip: '',
  phone: '',
  rep_name: '',
  rep_email: '',
  rep_phone: '',
  rep2_name: '',
  rep2_email: '',
  rep2_phone: '',
  inbound_routing: '',
  outbound_routing: '',
};

export const BankManagementPage = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [editingBank, setEditingBank] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('bankMgmt_viewMode') || 'grid');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Bank Transactions Modal
  const [showTrans, setShowTrans] = useState(false);
  const [transBankName, setTransBankName] = useState('');
  const [bankTrans, setBankTrans] = useState([]);
  const [transLoading, setTransLoading] = useState(false);
  const [tSortCol, setTSortCol] = useState('date');
  const [tSortDir, setTSortDir] = useState('desc');

  const openBankTrans = async (bankId, bankName) => {
    setTransBankName(bankName);
    setShowTrans(true);
    setTransLoading(true);
    try {
      const res = await bankApi.getTransactions(bankId);
      setBankTrans(res.data);
    } catch {
      toast.error('Failed to load transactions');
    }
    setTransLoading(false);
  };

  const sortedBankTrans = useMemo(() => {
    return [...bankTrans].sort((a, b) => {
      const dir = tSortDir === 'asc' ? 1 : -1;
      switch (tSortCol) {
        case 'date': return dir * (new Date(a.date) - new Date(b.date));
        case 'llc': return dir * (a.llc || '').localeCompare(b.llc || '');
        case 'payee': return dir * (a.payee || '').localeCompare(b.payee || '');
        case 'memo': return dir * (a.memo || '').localeCompare(b.memo || '');
        case 'amount': return dir * ((a.amount || 0) - (b.amount || 0));
        default: return 0;
      }
    });
  }, [bankTrans, tSortCol, tSortDir]);

  const toggleTSort = (col) => {
    if (tSortCol === col) {
      setTSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setTSortCol(col);
      setTSortDir('asc');
    }
  };

  const TSortHeader = ({ col, children, className = '' }) => (
    <th className={`cursor-pointer select-none hover:bg-slate-100 ${className}`} onClick={() => toggleTSort(col)}>
      <span className="inline-flex items-center gap-1">
        {children}
        {tSortCol === col ? (tSortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </span>
    </th>
  );

  const handleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('bankMgmt_viewMode', mode);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedBanks = [...banks].sort((a, b) => {
    let aVal = String(a[sortField] || '').toLowerCase();
    let bVal = String(b[sortField] || '').toLowerCase();
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-white" />
      : <ArrowDown className="w-3 h-3 text-white" />;
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      const response = await bankApi.list();
      setBanks(response.data);
    } catch (error) {
      toast.error('Failed to fetch banks');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Format phone numbers
    if (name === 'phone' || name === 'rep_phone' || name === 'rep2_phone') {
      setFormData(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleEdit = (bank) => {
    setEditingBank(bank);
    setFormData({
      name: bank.name,
      nickname: bank.nickname || '',
      address: bank.address || '',
      city_state_zip: bank.city_state_zip || '',
      phone: formatPhoneNumber(bank.phone || ''),
      rep_name: bank.rep_name || '',
      rep_email: bank.rep_email || '',
      rep_phone: formatPhoneNumber(bank.rep_phone || ''),
      rep2_name: bank.rep2_name || '',
      rep2_email: bank.rep2_email || '',
      rep2_phone: formatPhoneNumber(bank.rep2_phone || ''),
      inbound_routing: bank.inbound_routing || bank.routing_number || '',
      outbound_routing: bank.outbound_routing || bank.routing_number || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingBank) {
        await bankApi.update(editingBank.id, formData);
        toast.success('Bank updated successfully');
      } else {
        await bankApi.create(formData);
        toast.success('Bank created successfully');
      }
      setDialogOpen(false);
      setFormData(initialFormData);
      setEditingBank(null);
      fetchBanks();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingBank ? 'update' : 'create'} bank`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (bankId) => {
    try {
      await bankApi.delete(bankId);
      toast.success('Bank deleted successfully');
      fetchBanks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 fade-in" data-testid="bank-management-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Bank Management</h1>
          <p className="text-slate-500 mt-1">Manage your banking relationships</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-white border border-slate-200 rounded-lg p-0.5 shadow-sm">
            <button
              onClick={() => handleViewMode('grid')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'grid' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="bank-grid-view-btn"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleViewMode('list')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="bank-list-view-btn"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingBank(null);
            setFormData(initialFormData);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-bank-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Bank
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{editingBank ? 'Edit Bank' : 'Add New Bank'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Bank Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  data-testid="bank-name-input"
                />
              </div>

              <div>
                <Label htmlFor="nickname">Nickname</Label>
                <Input
                  id="nickname"
                  name="nickname"
                  value={formData.nickname}
                  onChange={handleInputChange}
                  placeholder="e.g. Operating Account, Payroll"
                  data-testid="bank-nickname-input"
                />
              </div>

              <div>
                <Label htmlFor="address">Bank Address</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="123 Main Street"
                  data-testid="bank-address-input"
                />
              </div>

              <div>
                <Label htmlFor="city_state_zip">City, State, ZIP</Label>
                <Input
                  id="city_state_zip"
                  name="city_state_zip"
                  value={formData.city_state_zip}
                  onChange={handleInputChange}
                  placeholder="Dallas, TX 75001"
                  data-testid="bank-city-input"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(214) 555-1234"
                  data-testid="bank-phone-input"
                />
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium text-sm text-slate-700 mb-3">Bank Representative 1</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="rep_name">Rep Name</Label>
                    <Input
                      id="rep_name"
                      name="rep_name"
                      value={formData.rep_name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      data-testid="bank-rep-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rep_email">Rep Email</Label>
                    <Input
                      id="rep_email"
                      name="rep_email"
                      type="email"
                      value={formData.rep_email}
                      onChange={handleInputChange}
                      placeholder="john@bank.com"
                      data-testid="bank-rep-email-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="rep_phone">Rep Phone</Label>
                    <Input
                      id="rep_phone"
                      name="rep_phone"
                      value={formData.rep_phone}
                      onChange={handleInputChange}
                      placeholder="(XXX) XXX-XXXX"
                      data-testid="bank-rep-phone-input"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium text-sm text-slate-700 mb-3">Bank Representative 2</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="rep2_name">Rep Name</Label>
                    <Input
                      id="rep2_name"
                      name="rep2_name"
                      value={formData.rep2_name}
                      onChange={handleInputChange}
                      placeholder="Jane Doe"
                      data-testid="bank-rep2-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rep2_email">Rep Email</Label>
                    <Input
                      id="rep2_email"
                      name="rep2_email"
                      type="email"
                      value={formData.rep2_email}
                      onChange={handleInputChange}
                      placeholder="jane@bank.com"
                      data-testid="bank-rep2-email-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="rep2_phone">Rep Phone</Label>
                    <Input
                      id="rep2_phone"
                      name="rep2_phone"
                      value={formData.rep2_phone}
                      onChange={handleInputChange}
                      placeholder="(XXX) XXX-XXXX"
                      data-testid="bank-rep2-phone-input"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium text-sm text-slate-700 mb-3">Routing Numbers</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="inbound_routing">Inbound Routing # *</Label>
                    <Input
                      id="inbound_routing"
                      name="inbound_routing"
                      value={formData.inbound_routing}
                      onChange={handleInputChange}
                      required
                      placeholder="9 digits"
                      data-testid="bank-inbound-routing-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="outbound_routing">Outbound Routing # *</Label>
                    <Input
                      id="outbound_routing"
                      name="outbound_routing"
                      value={formData.outbound_routing}
                      onChange={handleInputChange}
                      required
                      placeholder="9 digits"
                      data-testid="bank-outbound-routing-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="bank-submit-btn">
                  {submitting ? (editingBank ? 'Updating...' : 'Creating...') : (editingBank ? 'Update Bank' : 'Add Bank')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="border-slate-200">
        {viewMode === 'grid' && (
        <CardHeader className="bg-slate-800 rounded-t-lg pb-3">
          <CardTitle className="flex items-center gap-2 text-lg" style={{ color: '#ffffff' }}>
            <Building2 className="w-5 h-5" />
            Banks ({banks.length})
          </CardTitle>
        </CardHeader>
        )}
        <CardContent className={viewMode === 'list' ? 'p-0' : ''}>
          {banks.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No banks added yet. Click "Add Bank" to get started.
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {sortedBanks.map(bank => (
                <div
                  key={bank.id}
                  className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">
                        <button
                          className="text-blue-700 hover:text-blue-900 hover:underline cursor-pointer text-left"
                          onClick={(e) => { e.stopPropagation(); openBankTrans(bank.id, bank.name); }}
                          data-testid={`bank-name-link-${bank.id}`}
                        >
                          {bank.name}
                        </button>
                      </h3>
                      {bank.address && (
                        <p className="text-sm text-slate-600 truncate">
                          {bank.address}{bank.city_state_zip && `, ${bank.city_state_zip}`}
                        </p>
                      )}
                      {bank.phone && (
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                          <Phone className="w-3 h-3 flex-shrink-0" />
                          {formatPhoneNumber(bank.phone)}
                        </p>
                      )}
                      <div className="flex gap-4 mt-2 text-xs text-slate-400 font-mono">
                        <span>In: {bank.inbound_routing || bank.routing_number}</span>
                        <span>Out: {bank.outbound_routing || bank.routing_number}</span>
                      </div>
                      {(bank.rep_name || bank.rep_email) && (
                        <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
                          <span className="text-slate-400">Rep 1: </span>
                          {bank.rep_name && <span>{bank.rep_name}</span>}
                          {bank.rep_email && <span className="ml-2 text-slate-400">{bank.rep_email}</span>}
                        </div>
                      )}
                      {(bank.rep2_name || bank.rep2_email) && (
                        <div className="text-xs text-slate-500">
                          <span className="text-slate-400">Rep 2: </span>
                          {bank.rep2_name && <span>{bank.rep2_name}</span>}
                          {bank.rep2_email && <span className="ml-2 text-slate-400">{bank.rep2_email}</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(bank)} data-testid="edit-bank-btn" className="h-8 w-8 p-0">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 h-8 w-8 p-0" data-testid="delete-bank-btn">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Bank?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{bank.name}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(bank.id)} className="bg-red-600 hover:bg-red-700">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[calc(100vh-220px)] overflow-y-auto">
              <table className="data-table w-full">
                <thead className="sticky top-0 z-10">
                  <tr style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>
                    <th className="cursor-pointer select-none" onClick={() => handleSort('name')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center gap-1.5">Bank Name <SortIcon field="name" /></span>
                    </th>
                    <th className="cursor-pointer select-none" onClick={() => handleSort('nickname')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center gap-1.5">Nickname <SortIcon field="nickname" /></span>
                    </th>
                    <th className="cursor-pointer select-none" onClick={() => handleSort('address')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center gap-1.5">Address <SortIcon field="address" /></span>
                    </th>
                    <th style={{ color: '#ffffff', borderBottom: 'none' }}>Phone</th>
                    <th style={{ color: '#ffffff', borderBottom: 'none' }}>Inbound Routing</th>
                    <th style={{ color: '#ffffff', borderBottom: 'none' }}>Outbound Routing</th>
                    <th className="cursor-pointer select-none" onClick={() => handleSort('rep_name')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center gap-1.5">Rep <SortIcon field="rep_name" /></span>
                    </th>
                    <th className="text-right" style={{ color: '#ffffff', borderBottom: 'none' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedBanks.map(bank => (
                    <tr key={bank.id}>
                      <td className="font-semibold">
                        <button
                          className="text-blue-700 hover:text-blue-900 hover:underline cursor-pointer text-left"
                          onClick={() => openBankTrans(bank.id, bank.name)}
                          data-testid={`bank-name-link-list-${bank.id}`}
                        >
                          {bank.name}
                        </button>
                      </td>
                      <td>{bank.nickname || '-'}</td>
                      <td>{bank.address}{bank.city_state_zip ? `, ${bank.city_state_zip}` : ''}</td>
                      <td>{formatPhoneNumber(bank.phone) || '-'}</td>
                      <td>{bank.inbound_routing || bank.routing_number || '-'}</td>
                      <td>{bank.outbound_routing || bank.routing_number || '-'}</td>
                      <td>{bank.rep_name || '-'}</td>
                      <td className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(bank)} data-testid="edit-bank-btn">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" data-testid="delete-bank-btn">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Bank?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{bank.name}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(bank.id)} className="bg-red-600 hover:bg-red-700">
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bank Transactions Modal */}
      <Dialog open={showTrans} onOpenChange={setShowTrans}>
        <DialogContent className="max-w-5xl max-h-[80vh]" data-testid="bank-transactions-modal">
          <DialogHeader>
            <DialogTitle>Transactions — {transBankName}</DialogTitle>
          </DialogHeader>
          {transLoading ? (
            <div className="py-8 text-center text-slate-500">Loading transactions...</div>
          ) : sortedBankTrans.length === 0 ? (
            <div className="py-8 text-center text-slate-500">No transactions found for this bank.</div>
          ) : (
            <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
              <table className="data-table w-full">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr>
                    <th>Type</th>
                    <th>Ref #</th>
                    <TSortHeader col="date">Date</TSortHeader>
                    <TSortHeader col="llc">LLC</TSortHeader>
                    <TSortHeader col="payee">Payee</TSortHeader>
                    <TSortHeader col="memo">Memo</TSortHeader>
                    <TSortHeader col="amount" className="text-right">Amount</TSortHeader>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedBankTrans.map((t, i) => (
                    <tr key={i} className={t.status === 'voided' ? 'opacity-50 line-through' : ''}>
                      <td>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${t.type === 'check' ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`}>
                          {t.type === 'check' ? 'Check' : 'Wire'}
                        </span>
                      </td>
                      <td className="font-mono text-sm">{t.type === 'check' ? t.ref : '—'}</td>
                      <td className="text-sm">{t.date ? new Date(t.date).toLocaleDateString() : '—'}</td>
                      <td className="text-sm font-medium">{t.llc}</td>
                      <td className="text-sm">{t.payee}</td>
                      <td className="text-sm text-slate-600">{t.memo || '—'}</td>
                      <td className="text-sm text-right font-mono">${(t.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          t.status === 'printed' || t.status === 'sent' ? 'bg-green-50 text-green-700' :
                          t.status === 'voided' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
                        }`}>
                          {t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-sm text-slate-500 mt-3 px-2">
                {sortedBankTrans.length} transaction{sortedBankTrans.length !== 1 ? 's' : ''} — Total: ${sortedBankTrans.filter(t => t.status !== 'voided').reduce((s, t) => s + (t.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} (excluding voided)
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
